import api from './api';
import { AxiosResponse } from 'axios';

import {
  PhysicalUnit,
  LengthPhysicalUnit,
  TemperaturePhysicalUnit,
  PressurePhysicalUnit,
  VelocityPhysicalUnit,
  VolumetricFlowRatePhysicalUnit,
  ThermalPowerPhysicalUnit,
  HeatTransferCoefficientPhysicalUnit,
  RatioPhysicalUnit,
  DensityPhysicalUnit,
  DynamicViscosityPhysicalUnit,
  SpecificHeatCapacityPhysicalUnit,
  ThermalConductivityPhysicalUnit,
  SpecificThermalResistancePhysicalUnit,
  OptionalLengthPhysicalUnit,
  OptionalTemperaturePhysicalUnit,
  OptionalPressurePhysicalUnit,
  OptionalVelocityPhysicalUnit,
  OptionalVolumetricFlowRatePhysicalUnit,
  OptionalThermalPowerPhysicalUnit,
  OptionalHeatTransferCoefficientPhysicalUnit,
  OptionalRatioPhysicalUnit,
  OptionalDensityPhysicalUnit,
  OptionalDynamicViscosityPhysicalUnit,
  OptionalSpecificHeatCapacityPhysicalUnit,
  OptionalThermalConductivityPhysicalUnit,
  OptionalSpecificThermalResistancePhysicalUnit,
  TemperatureDifferencePhysicalUnit
} from '../components/common/types/physicalUnits';

// Re-export physical unit types for use in other components
export type {
  PhysicalUnit,
  LengthPhysicalUnit,
  TemperaturePhysicalUnit,
  PressurePhysicalUnit,
  VelocityPhysicalUnit,
  VolumetricFlowRatePhysicalUnit,
  ThermalPowerPhysicalUnit,
  HeatTransferCoefficientPhysicalUnit,
  RatioPhysicalUnit,
  DensityPhysicalUnit,
  DynamicViscosityPhysicalUnit,
  SpecificHeatCapacityPhysicalUnit,
  ThermalConductivityPhysicalUnit,
  SpecificThermalResistancePhysicalUnit,
  OptionalLengthPhysicalUnit,
  OptionalTemperaturePhysicalUnit,
  OptionalPressurePhysicalUnit,
  OptionalVelocityPhysicalUnit,
  OptionalVolumetricFlowRatePhysicalUnit,
  OptionalThermalPowerPhysicalUnit,
  OptionalHeatTransferCoefficientPhysicalUnit,
  OptionalRatioPhysicalUnit,
  OptionalDensityPhysicalUnit,
  OptionalDynamicViscosityPhysicalUnit,
  OptionalSpecificHeatCapacityPhysicalUnit,
  OptionalThermalConductivityPhysicalUnit,
  OptionalSpecificThermalResistancePhysicalUnit,
  TemperatureDifferencePhysicalUnit
};

// Type definitions
export interface EvaporatorFluidsAPI {
  refrigerant: string;
  medium: MediumAPI;
  fouling_factor_inside: SpecificThermalResistancePhysicalUnit;
  fouling_factor_outside: SpecificThermalResistancePhysicalUnit;
}

export interface MediumAPI {
  name: string;
  brine_concentration: RatioPhysicalUnit;
  custom_name?: string | null;
  reference_temperature?: OptionalTemperaturePhysicalUnit | null;
  custom_density?: OptionalDensityPhysicalUnit | null;
  custom_dynamic_viscosity?: OptionalDynamicViscosityPhysicalUnit | null;
  custom_specific_heat_capacity?: OptionalSpecificHeatCapacityPhysicalUnit | null;
  custom_thermal_conductivity?: OptionalThermalConductivityPhysicalUnit | null;
}

export interface WKEOperatingConditionsAPI {
  super_heating: TemperatureDifferencePhysicalUnit;
  vapor_quality: RatioPhysicalUnit;
}

export interface SelectionLabelsBoilingCoaxial {
  calculation_mode: string[];
  unit_system: string[];
  message_level: string[];
  refrigerant: string[];
  ratio_unit: string[];
  medium_type: string[];
  thermal_power_unit: string[];
  pressure_unit: string[];
  velocity_unit: string[];
  volumetric_flow_unit: string[];
  thermal_resistance_unit: string[];
  temperature_unit: string[];
  temperature_difference_unit: string[];
  heattransfer_coefficient_unit: string[];
  length_unit: string[];
  oil_viscosity: string[];
  heatflux_unit: string[];
  specific_heat_capacity_unit: string[];
  dynamic_viscosity_unit: string[];
  density_unit: string[];
  thermal_conductivity_unit: string[];
}

export interface InterChangableOperatingConditionsWKEAPI {
  temperature_medium_inlet?: OptionalTemperaturePhysicalUnit | null;
  temperature_medium_outlet?: OptionalTemperaturePhysicalUnit | null;
  capacity?: OptionalThermalPowerPhysicalUnit | null;
  volumetric_flow_rate?: OptionalVolumetricFlowRatePhysicalUnit | null;
  temperature_saturation_evaporation?: OptionalTemperaturePhysicalUnit | null;
}

export interface RequestBodyWKEHeatExchangerAPI {
  heat_exchanger_name: string;
  fluids: EvaporatorFluidsAPI;
  system_of_units: string;
  operating_conditions: WKEOperatingConditionsAPI;
  interchangable_input_conditions?: InterChangableOperatingConditionsWKEAPI | null;
  calculation_mode: string;
}

export interface OptionalRequestBodyWKEHeatExchangerAPI {
  heat_exchanger_name?: string | null;
  fluids?: OptionalEvaporatorFluidsAPI | null;
  system_of_units: string;
  interchangable_input_conditions?: InterChangableOperatingConditionsWKEAPI | null;
  operating_conditions?: WKEOperatingConditionsAPI | null;
  calculation_mode?: string | null;
}

export interface RequestBodySelectWKEHeatExchangerAPI {
  fluids: EvaporatorFluidsAPI;
  system_of_units: string;
  operating_conditions: WKEOperatingConditionsAPI;
  interchangable_input_conditions?: InterChangableOperatingConditionsWKEAPI | null;
  calculation_mode: string;
  selected_models: string[];
}

export interface Message {
  message_level: string;
  message_content: string;
}

// Type definition for refrigerant object
export interface RefrigerantResultAPI {
  name: string;
  saturation_pressure: OptionalPressurePhysicalUnit | null;
  critical_temperature: OptionalTemperaturePhysicalUnit | null;
  critical_pressure: OptionalPressurePhysicalUnit | null;
}

export interface MediumResultAPI {
  name: string;
  mean_temperature: OptionalTemperaturePhysicalUnit | null;
  thermal_conductivity: OptionalThermalConductivityPhysicalUnit | null;
  density: OptionalDensityPhysicalUnit | null;
  dynamic_viscosity: OptionalDynamicViscosityPhysicalUnit | null;
  kinematic_viscosity: OptionalDynamicViscosityPhysicalUnit | null;
  specific_heat_capacity: OptionalSpecificHeatCapacityPhysicalUnit | null;
  prandtl_number: number | null;
  brine_concentration?: OptionalRatioPhysicalUnit | null;
}

export interface HeatExchangerDimensionsAPI {
  install_dimension_a: LengthPhysicalUnit | null;
  install_dimension_b: LengthPhysicalUnit | null;
  install_dimension_h: LengthPhysicalUnit | null;
  connection_dimension_d1: LengthPhysicalUnit | null;
  connection_dimension_d2: LengthPhysicalUnit | null;
  connection_dimension_d3: LengthPhysicalUnit | null;
  connection_dimension_a: LengthPhysicalUnit | null;
  connection_dimension_h: LengthPhysicalUnit | null;
  volume_outside_medium: VolumetricFlowRatePhysicalUnit | null;
  volume_inside_medium: VolumetricFlowRatePhysicalUnit | null;
  weight: LengthPhysicalUnit | null;
  availability?: string;
  PED?: string;
}

export interface ResultBodyWKEHeatExchangerAPI {
  input_data?: any | null;
  messages?: Message[] | null;
  overall_message_level?: string | null;
  capacity?: ThermalPowerPhysicalUnit | null;
  volumetric_flow_rate?: VolumetricFlowRatePhysicalUnit | null;
  overall_heat_transfer_coefficient?: HeatTransferCoefficientPhysicalUnit | null;
  outside_pressure_drop?: PressurePhysicalUnit | null;
  outside_pressure_drop_total?: PressurePhysicalUnit | null;
  inside_heat_transfer_coefficient?: HeatTransferCoefficientPhysicalUnit | null;
  inside_temperature_mean?: TemperaturePhysicalUnit | null;
  outside_velocity?: VelocityPhysicalUnit | null;
  logarithmic_mean_temperature_difference?: TemperatureDifferencePhysicalUnit | null;
  outside_heat_transfer_coefficient?: HeatTransferCoefficientPhysicalUnit | null;
  outside_heat_flux?: ThermalPowerPhysicalUnit | null;
  over_design?: RatioPhysicalUnit | null;
  inside_fouling_resistance?: RatioPhysicalUnit | null;
  outside_fouling_resistance?: RatioPhysicalUnit | null;
  inside_htc_resistance?: RatioPhysicalUnit | null;
  outside_htc_resistance?: RatioPhysicalUnit | null;
  wall_resistance?: RatioPhysicalUnit | null;
  reynolds_number?: number | null;
  temperature_medium_inlet?: TemperaturePhysicalUnit | null;
  temperature_medium_outlet?: TemperaturePhysicalUnit | null;
  temperature_saturation_evaporation?: TemperaturePhysicalUnit | null;
  tube_length?: LengthPhysicalUnit | null;
  vapor_quality?: RatioPhysicalUnit | null;
  tube_count?: number | null;
  coil_diameter?: LengthPhysicalUnit | null;
  mantle_diameter?: LengthPhysicalUnit | null;
  super_heating?: TemperatureDifferencePhysicalUnit | null;
  material?: string | null;
  medium?: MediumResultAPI | null;
  refrigerant?: RefrigerantResultAPI | null;
  heat_exchanger_dimensions?: HeatExchangerDimensionsAPI | null;
}

export interface ExtendedResultBodyWKEHeatExchangerAPI extends ResultBodyWKEHeatExchangerAPI {
  timestamp?: string;
}

// Optional models
export interface OptionalEvaporatorFluidsAPI {
  refrigerant?: string | null;
  medium?: OptionalMediumAPI | null;
  fouling_factor_inside?: OptionalSpecificThermalResistancePhysicalUnit | null;
  fouling_factor_outside?: OptionalSpecificThermalResistancePhysicalUnit | null;
}

export interface OptionalMediumAPI {
  name?: string | null;
  brine_concentration?: OptionalRatioPhysicalUnit | null;
  custom_name?: string | null;
  reference_temperature?: OptionalTemperaturePhysicalUnit | null;
  custom_density?: OptionalDensityPhysicalUnit | null;
  custom_dynamic_viscosity?: OptionalDynamicViscosityPhysicalUnit | null;
  custom_specific_heat_capacity?: OptionalSpecificHeatCapacityPhysicalUnit | null;
  custom_thermal_conductivity?: OptionalThermalConductivityPhysicalUnit | null;
}

export interface OptionalWKEOperatingConditionsAPI {
  super_heating?: TemperatureDifferencePhysicalUnit | null;
  vapor_quality?: OptionalRatioPhysicalUnit | null;
}

/**
 * Comprehensive service for the Coaxial Evaporator calculator
 * Combines both UI and calculation functionality
 */
const coaxialEvaporatorService = {
  // ======================================================
  // UI Related API Endpoints
  // ======================================================
  
  /**
   * Gets the input labels and dropdown options for the UI
   */
  getInputLabels: (): Promise<AxiosResponse<SelectionLabelsBoilingCoaxial>> =>
    api.get<SelectionLabelsBoilingCoaxial>('/v1/heat-exchanger/coaxial-evaporator/model/ui/input-labels'),

  /**
   * Gets the list of available refrigerants for WKE heat exchangers
   */
  getRefrigerants: (): Promise<AxiosResponse<string[]>> =>
    api.get<string[]>('/v1/heat-exchanger/coaxial-evaporator/model/ui/refrigerants'),

  /**
   * Converts values in the request body to the specified unit system
   * @param formValues Request body to convert
   */
  convertValues: (formValues: OptionalRequestBodyWKEHeatExchangerAPI): Promise<AxiosResponse<OptionalRequestBodyWKEHeatExchangerAPI>> =>
    api.post<OptionalRequestBodyWKEHeatExchangerAPI>('/v1/heat-exchanger/coaxial-evaporator/model/ui/convert-values', formValues),

  // ======================================================
  // Calculation API Endpoints
  // ======================================================
  
  /**
   * Performs the evaporator calculation
   * @param data Calculation input data
   */
  calculateEvaporator: (data: RequestBodyWKEHeatExchangerAPI): Promise<AxiosResponse<ResultBodyWKEHeatExchangerAPI>> => 
    api.post<ResultBodyWKEHeatExchangerAPI>('/v1/heat-exchanger/coaxial-evaporator/model/calculate', data),

  /**
   * Selects and calculates results for multiple WKE heat exchanger models
   * @param data Calculation input data
   */
  selectEvaporators: (data: RequestBodySelectWKEHeatExchangerAPI): Promise<AxiosResponse<ResultBodyWKEHeatExchangerAPI[]>> => 
    api.post<ResultBodyWKEHeatExchangerAPI[]>('/v1/heat-exchanger/coaxial-evaporator/model/select', data),

  // ======================================================
  // Export API Endpoints
  // ======================================================
  
  /**
   * Generates Excel results file
   * @param results Results to export
   * @param name Optional filename
   */
  generateExcelResults: (
    results: ResultBodyWKEHeatExchangerAPI[], 
    name: string = 'coaxial_evaporator_results'
  ): Promise<AxiosResponse<Blob>> => 
    api.post<Blob>(
      '/v1/heat-exchanger/coaxial-evaporator/model/ui/download/results-excel-file',
      results,
      {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      }
    ),

  /**
   * Generates a PDF report
   * @param result Result to generate PDF for
   * @param name Optional filename
   * @param share_url Optional share URL generated by frontend
   * @param project_info Optional project information
   */
  generatePDF: (result: ResultBodyWKEHeatExchangerAPI, name: string = 'coaxial_evaporator_results', share_url?: string, project_info?: any): Promise<AxiosResponse<Blob>> => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    
    const payload = {
      result,
      project_info,
      share_url
    };
    
    return api.post<Blob>(
      `/v1/heat-exchanger/coaxial-evaporator/model/ui/download/pdf?${params.toString()}`,
      payload,
      {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      }
    );
  },

  convertTDUFile: (tduData: any): Promise<AxiosResponse<OptionalRequestBodyWKEHeatExchangerAPI>> => 
    api.post<OptionalRequestBodyWKEHeatExchangerAPI>('/v1/heat-exchanger/coaxial-evaporator/model/ui/convert-tdu', tduData),
};

export default coaxialEvaporatorService;
